import { inject, NgModule } from '@angular/core';
import { RouterModule, Routes, PreloadAllModules } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';

import { IRouteData, RoleList } from './models/role';
import { MainLayoutComponent } from './shared/layouts/main/main.component';
import { ConfigurationService } from './services/configuration/configuration.service';
import { Store } from '@ngxs/store';
import { filter } from 'rxjs/operators';

const configInitialized = () => inject(Store).select<boolean>(ConfigurationService.initializedSelector).pipe(filter(_ => _ === true));

const routes: Routes = [
    {
        path: '',
        component: MainLayoutComponent,
        canActivate: [MsalGuard, configInitialized ],
        canActivateChild: [MsalGuard, configInitialized],
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: '/dashboard'
            },

            {
                path: 'admin',
                loadChildren: () => import('./pages/admin/admin.module').then(m => m.AdminModule),
                data: { title: 'Admin', menu: 'admin', allowedRoles: [RoleList.Administrator] } as IRouteData,
            },

            {
                path: 'builder',
                loadChildren: () => import('./pages/templates/templates.module').then(m => m.TemplatesModule),
                data: { title: 'Documents', allowedRoles: [RoleList.Administrator] } as IRouteData,
            },

            {
                path: 'configs',
                loadChildren: () => import('./pages/config-manager/config-manager.module').then(m => m.ConfigManagerModule),
                data: { title: 'Configurations', allowedRoles: [RoleList.Administrator] } as IRouteData,
            },

            {
                path: 'templates',
                loadChildren: () => import('./pages/templates/templates.module').then(m => m.TemplatesModule),
                data: { title: 'Templates', allowedRoles: [RoleList.Administrator] } as IRouteData,
            },

            {
                path: 'profile-manager',
                loadChildren: () => import('./pages/profile-manager/profile-manager.module').then(m => m.ProfileManagerModule),
                data: { title: 'Profiles'} as IRouteData,
            },

            {
                path: 'userviews',
                loadChildren: () => import('./pages/mywork/user-views/user-views.module').then(m => m.UserViewsModule),
                data: { title: 'UserViews' } as IRouteData,
            },

            {
                path: 'messages',
                loadChildren: () => import('./pages/mywork/notification-items/notification-items.module').then(m => m.NotificationItemsModule),
                data: { title: 'Messages' } as IRouteData,
            },

            {
                path: 'processview',
                loadChildren: () => import('./pages/mywork/process-queue/+view-components/process-view-items/process-view-items/process-view-items.module').then(m => m.ProcessViewItemsModule),
                data: { title: 'ProcessView' } as IRouteData,
            },

            {
                path: 'report',
                loadChildren: () => import('./shared/reports/powerbi-outlet/powerbi-outlet.module').then(m => m.PowerBiOutletModule),
                data: { title: 'Reports' } as IRouteData,
            },

            {
                path: 'workitemselect',
                loadChildren: () => import('./pages/mywork/work-items-selection/work-items-selection.module').then(m => m.WorkItemsSelectionModule),
                data: { title: 'WorkItemSelection' } as IRouteData,
            },

            {
                path: 'workitem',
                loadChildren: () => import('./pages/mywork/work-items-selection/work-items-selection.module').then(m => m.WorkItemsSelectionModule),
                data: { title: 'WorkItems' } as IRouteData,
            },

            {
                path: 'myworkitems',
                loadChildren: () => import('./pages/mywork/work-items/work-items-own-wrapper/work-items-own-wrapper.module').then(m => m.WorkItemsOwnWrapperModule),
                data: { title: 'WorkItems' } as IRouteData,
            },

            {
                path: 'orchestration',
                loadChildren: () => import('./pages/orchestration/orchestration.module').then(m => m.OrchestrationModule),
                data: { title: 'Orchestration', allowedRoles: [RoleList.Administrator] } as IRouteData,
            },
            {
                path: 'user-manager',
                loadChildren: () => import('./pages/user-manager/user-manager.module').then(m => m.UserManagerModule),
                data: { allowedRoles: [RoleList.Administrator] } as IRouteData
            },
            {
                path: 'dashboard',
                loadChildren: () => import('./pages/dashboard/dashboard.module').then(m => m.DashboardModule),
                data: { title: 'Dashboard' } as IRouteData,
            },
            {
                path: 'tenant',
                loadChildren: () => import('./pages/tenant/tenant.module').then(m => m.TenantModule),
                data: { title: 'Tenant' } as IRouteData,
            },
            {
                path: 'government-relations',
                loadChildren: () => import('./pages/government-relations/government-relations.module').then(m => m.GovernmentRelationsModule),
                data: { title: 'Government relations', menu: 'government_relations' } as IRouteData,
            },
            {
                path: 'templates',
                loadChildren: () => import('./pages/templates/templates.module').then(m => m.TemplatesModule),
                data: { title: 'Templates', allowedRoles: [RoleList.Administrator] } as IRouteData,
            },
            {
                path: 'facility/request',
                loadChildren: () => import('./pages/request/request.module').then(m => m.FacilityRequestListModule),
                data: {} as IRouteData,
            },

            {
                path: 'interviews',
                loadChildren: () => import('./pages/_searchs/interview-test/interview-test.module').then(m => m.InterviewTestModule),
                canActivate: [],
            },

            {
                path: 'member',
                loadChildren: () => import('./pages/member/member.module').then(m => m.MemberModule),
                data: { title: 'Member', allowedRoles: [RoleList.Member] } as IRouteData
            },

            {
                path: 'search',
                loadChildren: () => import('./pages/_searchs/member/member-search.module').then(m => m.MemberSearchModule),
                data: { title: 'Search Member', allowedRoles: [RoleList.Member] } as IRouteData
            },
            {
                path: 'document-type',
                loadChildren: () => import('./pages/document-types/document-types.module').then(m => m.DocumentTypesManagerModule),
                data: { allowedRoles: [RoleList.Administrator] } as IRouteData
            },

        ]
    },

    {
        path: '**',
        redirectTo: 'error/404'
    }
];

@NgModule({
    imports: [
        environment.production ? RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled', preloadingStrategy: PreloadAllModules })
            : RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled', enableTracing: false, preloadingStrategy: PreloadAllModules },)
    ],
    exports: [RouterModule],
    providers: [
        // { provide: LocationStrategy, useClass: HashLocationStrategy }
    ]
})
export class AppMainRoutingModule { }
